// Utilidades de impresión de ticket 80mm para comandera térmica (ESC/POS).
//
// El template está definido en `lib/labels/posTicket80.js` y su config viene
// del endpoint `/api/label-templates/public/pos_ticket_80mm` (editable desde
// Administración → Edición de Etiquetas).

import api from "./api";
import { renderPosTicket80Html } from "./labels/posTicket80";
import { renderPosTicketFiscal80Html } from "./labels/posTicketFiscal80";
import { renderAlivioCaja80Html } from "./labels/posAlivioCaja80";

let cachedHeader = null;
let cachedLogoDataUrl = null;
let cachedConfig = null;
let cachedConfigAt = 0;
let cachedFiscalConfig = null;
let cachedFiscalConfigAt = 0;
let cachedAlivioConfig = null;
let cachedAlivioConfigAt = 0;
const CONFIG_TTL_MS = 60_000; // 1 min

async function loadEmpresaHeader() {
  if (cachedHeader) return cachedHeader;
  try {
    const { data } = await api.get("/empresa/header");
    cachedHeader = data || {};
  } catch (_) {
    cachedHeader = {};
  }
  return cachedHeader;
}

async function loadLogoDataUrl() {
  if (cachedLogoDataUrl !== null) return cachedLogoDataUrl;
  try {
    const resp = await api.get("/empresa/logo", { responseType: "blob" });
    const blob = resp.data;
    cachedLogoDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    cachedLogoDataUrl = "";
  }
  return cachedLogoDataUrl;
}

async function loadTicketConfig() {
  const now = Date.now();
  if (cachedConfig && now - cachedConfigAt < CONFIG_TTL_MS) return cachedConfig;
  try {
    const { data } = await api.get("/label-templates/public/pos_ticket_80mm");
    cachedConfig = data?.config || {};
    cachedConfigAt = now;
  } catch (_) {
    cachedConfig = {};
  }
  return cachedConfig;
}

async function loadFiscalTicketConfig() {
  const now = Date.now();
  if (cachedFiscalConfig && now - cachedFiscalConfigAt < CONFIG_TTL_MS) return cachedFiscalConfig;
  try {
    const { data } = await api.get("/label-templates/public/pos_ticket_fiscal_80mm");
    cachedFiscalConfig = data?.config || {};
    cachedFiscalConfigAt = now;
  } catch (_) {
    cachedFiscalConfig = {};
  }
  return cachedFiscalConfig;
}

function _printHtmlIframe(html, iframeId = "__ticket_print_iframe__") {
  // Iframe oculto para no perder foco del POS.
  const prev = document.getElementById(iframeId);
  if (prev) prev.remove();

  const iframe = document.createElement("iframe");
  iframe.id = iframeId;
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Cleanup a los 30s (por si el usuario dejó el diálogo abierto).
  setTimeout(() => {
    try { iframe.remove(); } catch (_) {}
  }, 30000);
}

/**
 * Imprime un ticket de Delivery 80mm para un pedido "A Mostrador".
 * @param {object} pedido — item devuelto por GET /pos/delivery
 * @param {{modificacion?: boolean}} [opts]
 */
export async function imprimirTicketDelivery(pedido, opts = {}) {
  const [emp, logo, config] = await Promise.all([
    loadEmpresaHeader(),
    loadLogoDataUrl(),
    loadTicketConfig(),
  ]);

  const html = renderPosTicket80Html(pedido, config, {
    autoprint: true,
    logo,
    empresa: emp,
    modificacion: !!opts.modificacion,
  });

  _printHtmlIframe(html, "__ticket_print_iframe__");
}

/**
 * Imprime un Ticket Fiscal 80mm (Factura A/B/C con CAE de AFIP).
 * Se dispara automáticamente desde el POS al cerrar una venta con CAE.
 *
 * @param {object} factura — documento devuelto por POST /facturas (con CAE, tipo_comprobante, numero, items, iva, total, contraparte_*)
 * @param {{metodo_pago?: string, vuelto?: number}} [opts] — datos adicionales del cobro (opcionales)
 */
export async function imprimirTicketFiscal(factura, opts = {}) {
  const [emp, logo, config] = await Promise.all([
    loadEmpresaHeader(),
    loadLogoDataUrl(),
    loadFiscalTicketConfig(),
  ]);

  const enriched = {
    ...factura,
    metodo_pago: opts.metodo_pago || factura.metodo_pago || "",
    vuelto: opts.vuelto ?? factura.vuelto ?? 0,
  };

  const html = renderPosTicketFiscal80Html(enriched, config, {
    autoprint: true,
    logo,
    empresa: emp,
  });

  _printHtmlIframe(html, "__ticket_fiscal_print_iframe__");
}

async function loadAlivioConfig() {
  const now = Date.now();
  if (cachedAlivioConfig && now - cachedAlivioConfigAt < CONFIG_TTL_MS) return cachedAlivioConfig;
  try {
    const { data } = await api.get("/label-templates/public/pos_alivio_caja_80mm");
    cachedAlivioConfig = data?.config || {};
    cachedAlivioConfigAt = now;
  } catch (_) {
    cachedAlivioConfig = {};
  }
  return cachedAlivioConfig;
}

/**
 * Imprime un ticket de Alivio de Caja 80mm.
 * Se dispara al confirmar el Alivio de Caja desde el POS.
 *
 * @param {object} alivio — { fecha, sucursal, caja, usuario, comentario, movimiento, monto }
 */
export async function imprimirTicketAlivio(alivio) {
  const config = await loadAlivioConfig();
  const html = renderAlivioCaja80Html(alivio, config, { autoprint: true });
  _printHtmlIframe(html, "__ticket_alivio_print_iframe__");
}
